import {
  useEffect,
  useState,
} from 'react';
import { useMutation } from '@apollo/client';
import { Map, List }   from 'immutable';
import isEmpty         from 'lodash/isEmpty';

import { FileChecksum } from '@rails/activestorage/src/file_checksum';

import useBlobUpload from 'helpers/direct_upload/blob_upload';

import { DIRECT_UPLOAD_MUTATION } from 'queries/prompt/response';
import { DIRECT_UPLOAD_CAMPAIGN_MUTATION, DIRECT_UPLOAD_COMPANY_MUTATION } from 'queries/tenant/direct_upload';

const QUERY_MAP = {
  response: { query: DIRECT_UPLOAD_MUTATION, node: 'directUpload' },
  campaign: { query: DIRECT_UPLOAD_CAMPAIGN_MUTATION, node: 'directUploadCampaign' },
  company:  { query: DIRECT_UPLOAD_COMPANY_MUTATION, node: 'directUploadCompany' },
};

const DEFAULT_LEGACY_ACTIVE_STORAGE = ['response'];

function useBlobRecord(blobs, key) {
  const [state, setState] = useState(new Map());

  const variable = new Map({
    filename:    '',
    contentType: '',
    byteSize:    '',
    checksum:    '',
  });

  const variables = state.get('variables', new List([]));

  const returningHash  = state.get('returningHash', {});

  const [directUpload, { data }] = useMutation(QUERY_MAP[key].query);
  const [setHash] = useBlobUpload();

  useEffect(() => {
    if (isEmpty(blobs)) return;

    let attrs = new List([]);

    blobs.forEach((blob) => {
      // This block is asynchronous
      FileChecksum.create(blob, (err, checksum) => {
        const extension = blob.type.split('/').pop();
        const filename  = blob.name ? blob.name : `file-${Date.now()}.${extension}`;

        if (err || checksum === '') {
          console.error('file-checksum-blob-record-error', err);
          Sentry.captureException(err, { tags: { filename, size: blob.size, type: blob.type } });
          return;
        }

        let newvar = variable.set('checksum', checksum)
          .set('filename', filename)
          .set('byteSize', blob.size)
          .set('contentType', blob.type);

        if (!DEFAULT_LEGACY_ACTIVE_STORAGE.includes(key)) newvar = newvar.set('serviceName', 'awscdn');

        attrs = attrs.push(newvar);

        setState(state.set('variables', attrs));
      });
    });
  }, [blobs]);

  useEffect(() => {
    // TODO: Size matching is to support multi files. Needs a better way.
    if (variables.size === 0 || variables.size !== blobs.length) return;

    // GQL Request will only be triggered once per batch even if this
    //   useEffect will trigger multiple times
    directUpload({
      variables:
      {
        file: variables.toJS(),
      },
    });
  }, [variables]);

  useEffect(() => {
    if (!data) return;

    const { metadata } = data.tenant[QUERY_MAP[key].node];

    const promises = metadata.map((meta, i) => {
      // TODO: Needs a better way, for now we map the response data to blobs by position.
      //   It will fail if checksum generation above failed on a specific file
      return setHash(meta, blobs[i]);
    });

    Promise.all(promises).then(() => {
      setState(state.set('returningHash', metadata));
    }).catch((err) => {
      console.error('blob-upload', err);
      if (Sentry) Sentry.captureException(err);
      setState(state.set('returningHash', null));
    });
  }, [data]);

  return [variables, returningHash];
}

export default useBlobRecord;
